import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout.js";
import { apiFetch } from "../../api.js";

const ColorMasterGrid = () => {
  const navigate = useNavigate();
  const [gridData, setGridData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchColor, setSearchColor] = useState("");
  const [searchType, setSearchType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchGridData = async () => {
    try {
      const resp = await apiFetch(
        "/api/colormaster/grid",
      );
      const data = await resp.json();
      setGridData(data);
      setFilteredData(data);
    } catch (err) {
      console.error("Error fetching Color Master grid:", err);
      alert("Unable to fetch data from backend.");
    }
  };

  useEffect(() => {
    fetchGridData();
  }, []);

  const handleSearch = async () => {
    try {
      const resp = await apiFetch(
        `/api/colormaster/search?q=${encodeURIComponent(
          searchColor,
        )}`,
      );
      const data = await resp.json();
      setFilteredData(data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error searching Color Master grid:", err);
      alert("Unable to fetch search results from backend.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this color?")) return;

    try {
      const res = await apiFetch(
        `/api/colormaster/delete/${id}`,
        {
          method: "DELETE",
        },
      );
      const data = await res.json();
      const { status, message } = data;

      if (status === "success") {
        alert("Color deleted successfully.");
        fetchGridData();
      } else {
        alert(message || "Unable to delete color.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete color. Please try again.");
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await apiFetch(
        `/api/colormaster/edit/${id}`,
      );

      // ✅ check response
      if (!res.ok) {
        const errText = await res.text();
        console.error("EDIT FETCH ERROR:", errText);
        alert("Failed to fetch record!");
        return;
      }

      const data = await res.json();

      const { mainData, childData } = data;

      const childRows = (childData || []).map((row) => ({
        model: row.model,
        chkbox: row.chkbox === true || row.chkbox === "Y",
      }));

      const editData = {
        sp_147: mainData?.sp_147 || "",
        sp_148: mainData?.sp_148 || "",
        sp_149: mainData?.sp_149 || "",
        sp_142: mainData?.sp_142 || "",
        childRows,
      };

      navigate("/Color", { state: { editData } });
    } catch (err) {
      console.error("Failed to fetch record:", err);
      alert("Unable to fetch record from backend.");
    }
  };
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  /*------------------- Styles--------------*/
  const styles = {
    pageContainer: {
      width: "100%",
      minHeight: "100vh",
      backgroundColor: "#f4f6f8",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "10px",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1200px",
      padding: "15px",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      backgroundColor: "#fff",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "15px",
      gap: "10px",
    },
    leftHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "8px",
      flex: 1,
      minWidth: "250px",
    },
    title: { fontSize: "1.2rem", fontWeight: "bold", flexShrink: 0 },
    addButton: {
      padding: "8px 16px",
      backgroundColor: "#3a28a0ff",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    searchInput: {
      flex: "1",
      minWidth: "140px",
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #ccc",
    },
    searchButton: {
      padding: "8px 12px",
      backgroundColor: "#2196F3",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    select: {
      padding: "8px 10px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      backgroundColor: "#fff",
      whiteSpace: "nowrap",
    },
    tableWrapper: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", minWidth: "400px" },
    th: {
      padding: "10px",
      textAlign: "left",
      backgroundColor: "#8B1E3F",
      color: "#fff",
      border: "1px solid #ddd",
      whiteSpace: "nowrap",
      fontSize: "0.9rem",
    },
    td: {
      padding: "8px",
      border: "1px solid #ddd",
      wordWrap: "break-word",
      fontSize: "0.85rem",
    },
    actionButton: (bgColor) => ({
      padding: "6px 10px",
      marginRight: "5px",
      backgroundColor: bgColor,
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      minWidth: "60px",
      fontSize: "0.8rem",
    }),
    pagination: {
      marginTop: "12px",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      fontSize: "0.9rem",
    },
    pageBtn: {
      padding: "6px 12px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      cursor: "pointer",
      background: "#f9f9f9",
      fontSize: "0.85rem",
    },
  };

  return (
    <Layout>
      <div style={styles.pageContainer}>
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.leftHeader}>
              <div style={styles.title}>Color Master Grid</div>

              <input
                type="text"
                placeholder="Search..."
                value={searchColor}
                onChange={(e) => setSearchColor(e.target.value)}
                style={styles.searchInput}
              />

              <button style={styles.searchButton} onClick={handleSearch}>
                Search
              </button>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={styles.select}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
            <button style={styles.addButton} onClick={() => navigate("/Color")}>
              Add New
            </button>
          </div>

          {/* Table */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>COLOR</th>
                  <th style={styles.th}>TYPE</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.sp_142}>
                      <td style={styles.td}>{row.sp_147}</td>
                      <td style={styles.td}>{row.sp_148}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.actionButton("#4CAF50")}
                          onClick={() => handleEdit(row.sp_142)}
                        >
                          Edit
                        </button>
                        <button
                          style={styles.actionButton("#f44336")}
                          onClick={() => handleDelete(row.sp_142)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={styles.td} colSpan={3}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              style={styles.pageBtn}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ColorMasterGrid;
